/**
 * JournalSubscribeStrip — Editorial subscribe block placed between LatestFromJournal
 * and KGOBCares on the homepage.
 *
 * Visual intent:
 *   - Reads as a continuation of The Journal's editorial voice, not a marketing ad
 *   - Paper-textured ivory background, hairline gold rules, restrained color palette
 *   - Inline email capture → opens SubscribeModal with email prefilled (double opt-in preserved)
 *   - Honeypot + light client-side validation; rate limit + suppression handled by backend
 */

import { useState } from 'react';
import { motion } from 'framer-motion';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import SubscribeModal from '@/components/subscriptions/SubscribeModal';

export const JournalSubscribeStrip = () => {
  const [email, setEmail] = useState('');
  const [honeypot, setHoneypot] = useState('');
  const [modalOpen, setModalOpen] = useState(false);
  const [error, setError] = useState('');

  const openWithPrefill = (e) => {
    e?.preventDefault();
    if (honeypot) return; // silent for bots
    const trimmed = email.trim();
    if (!trimmed || !trimmed.includes('@') || trimmed.length < 5) {
      setError('Please enter a valid email.');
      return;
    }
    setError('');
    setModalOpen(true);
  };

  return (
    <section
      className="relative bg-[linear-gradient(180deg,#F4EFE3_0%,#F7F2E6_55%,#EFE9D9_100%)] py-20 lg:py-28 overflow-hidden"
      data-testid="journal-subscribe-strip"
      aria-labelledby="journal-subscribe-heading"
    >
      {/* Hairline gold rules anchor the strip — top + bottom */}
      <div aria-hidden="true" className="absolute top-0 inset-x-0 h-px bg-[#D4AF37]/40" />
      <div aria-hidden="true" className="absolute bottom-0 inset-x-0 h-px bg-[#D4AF37]/30" />

      {/* Diffuse gold + navy radial accents for paper texture */}
      <div
        aria-hidden="true"
        className="pointer-events-none absolute inset-0 opacity-[0.06]"
        style={{
          backgroundImage:
            'radial-gradient(circle at 20% 30%, #D4AF37 0%, transparent 35%), radial-gradient(circle at 82% 75%, #0B2240 0%, transparent 40%)',
        }}
      />

      <SubscribeModal
        open={modalOpen}
        onClose={() => setModalOpen(false)}
        listId="journal"
        source="home_journal_strip"
        prefillEmail={email}
      />

      <div className="relative mx-auto max-w-3xl px-6 sm:px-8 lg:px-10">
        <motion.div
          initial={{ opacity: 0, y: 14 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: '-60px' }}
          transition={{ duration: 0.55 }}
          className="text-center"
        >
          <div className="flex items-center justify-center gap-3 mb-6">
            <span className="h-px w-8 bg-[#D4AF37]" />
            <span className="font-['Inter'] text-[11px] font-semibold tracking-[0.22em] uppercase text-[#D4AF37]">
              The KGOB Journal
            </span>
            <span className="h-px w-8 bg-[#D4AF37]" />
          </div>

          <h2
            id="journal-subscribe-heading"
            className="font-['Playfair_Display'] text-3xl sm:text-4xl lg:text-[2.6rem] text-[#0B2240] leading-[1.18] tracking-[-0.005em] mb-5"
          >
            Receive the next issue in your inbox.
          </h2>

          <p className="font-['Playfair_Display'] italic text-base sm:text-lg text-[#0B2240]/75 leading-relaxed max-w-xl mx-auto mb-9">
            Quarterly essays on the parts of tax law that actually move money &mdash; written by KGOB partners, no fluff, no marketing in between.
          </p>
        </motion.div>

        <motion.form
          onSubmit={openWithPrefill}
          initial={{ opacity: 0, y: 10 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: '-60px' }}
          transition={{ duration: 0.55, delay: 0.15 }}
          className="max-w-xl mx-auto"
          data-testid="journal-strip-form"
        >
          {/* Honeypot */}
          <input data-testid="journal-subscribe-strip-input-1"
            type="text"
            name="website"
            tabIndex={-1}
            autoComplete="off"
            value={honeypot}
            onChange={(e) => setHoneypot(e.target.value)}
            style={{ position: 'absolute', left: '-9999px', width: 1, height: 1, opacity: 0 }}
            aria-hidden="true"
          />

          <div className="flex flex-col sm:flex-row gap-3 sm:gap-2">
            <Input
              type="email"
              placeholder="you@firm.com"
              value={email}
              maxLength={254}
              onChange={(e) => {
                setEmail(e.target.value);
                setError('');
              }}
              required
              aria-label="Email address"
              data-testid="journal-strip-email"
              className="flex-1 h-12 bg-white/85 border-[#D4AF37]/30 focus-visible:ring-[#D4AF37] focus-visible:border-[#D4AF37] text-[#0B2240] placeholder:text-[#0B2240]/40"
            />
            <Button
              type="submit"
              className="h-12 px-7 bg-[#0B2240] hover:bg-[#0B2240]/90 text-[#F4EFE3] tracking-[0.06em] font-semibold whitespace-nowrap"
              data-testid="journal-strip-subscribe"
            >
              Subscribe
            </Button>
          </div>

          {error && (
            <p
              className="mt-3 text-sm text-[#B45F3E] text-center"
              role="alert"
              data-testid="journal-strip-error"
            >
              {error}
            </p>
          )}

          <p className="mt-5 text-center text-[11px] tracking-[0.12em] uppercase text-[#0B2240]/55">
            One-click unsubscribe &middot; No sharing or selling &middot; Six-digit confirmation
          </p>
        </motion.form>
      </div>
    </section>
  );
};

export default JournalSubscribeStrip;
